import { PropsWithChildren, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useDashboardLeaguesControllerRefs } from "@/api/__generated__/dashboard-leagues/dashboard-leagues";
import type { FederationRef, LeagueRef } from "@/api/__generated__/schemas";
import { useCurrentOrganizationContext } from "@/contexts/CurrentOrganizationContext";
// Deep import avoids a circular dependency: `@/contexts` / `@/hooks` barrels
// transitively re-export modules that depend on this provider.
import { useCurrentPlatformContext } from "@/contexts/CurrentPlatformContext";
import { useLocalStorage } from "@/hooks/common/useLocalStorage";
import { useScopedRouteParams } from "@/hooks/common/useScopedRouteParams";
import { mapToCapabilitySet, mapToOrganizationReference } from "@/mappers";
import { OrganizationReference } from "@/types/organization.types";
import { buildScopedPath } from "@/utils/common";

import { CurrentLeagueContext, ResolvedLeague } from "./CurrentLeagueContext";

// TODO(phase3c): `owner` lands on `LeagueRef` after the backend change + codegen.
// Until then read it defensively off a widened ref shape (no `as any`). The module
// toggles are widened to optional too so a ref predating them defaults to enabled.
type LeagueRefWithOwner = Omit<LeagueRef, "enableRankings" | "enableFactSheet"> & {
  owner?: FederationRef | null;
  enableRankings?: boolean;
  enableFactSheet?: boolean;
};

const readLeagueOwner = (ref: LeagueRefWithOwner): OrganizationReference | null =>
  ref.owner ? mapToOrganizationReference(ref.owner) : null;

export const CurrentLeagueProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const scopedParams = useScopedRouteParams();
  const { platform: currentPlatform } = useCurrentPlatformContext();
  const { organization: currentOrganization } = useCurrentOrganizationContext();

  const [storedLeagueId, setStoredLeagueId, removeStoredLeagueId] = useLocalStorage<string | null>(
    "leagueId",
    null,
    { subscribe: "currentDocument" }
  );

  // The URL is authoritative on a scoped URL: when it carries no league segment
  // there is intentionally no league, so switching platform/organization (both
  // drop the segment — a league belongs to a single org) resets the league rather
  // than the stale persisted id re-injecting it. The persisted id is only a
  // fallback for an unscoped/cold load, before the enforcer canonicalizes the URL.
  const leagueId: string | null = scopedParams.isScoped ? scopedParams.leagueId : storedLeagueId;

  const {
    data: refsData,
    isLoading,
    error,
  } = useDashboardLeaguesControllerRefs(
    { ids: leagueId !== null ? [leagueId] : [], withCapabilities: true },
    { query: { enabled: leagueId !== null } }
  );

  const league = useMemo<ResolvedLeague | null>(() => {
    if (leagueId === null) {
      return null;
    }

    const ref: LeagueRefWithOwner | undefined =
      refsData?.find((item) => item.id === leagueId) ?? refsData?.[0];

    if (!ref) {
      return null;
    }

    return {
      id: ref.id,
      title: ref.title,
      logo: ref.logo,
      owner: readLeagueOwner(ref),
      // Default to enabled so a ref predating these fields never accidentally
      // hides a module (the backend now always sends them).
      enableRankings: ref.enableRankings ?? true,
      enableFactSheet: ref.enableFactSheet ?? true,
      // Empty until the BE ships `capabilities` on the ref — under strict RBAC an
      // empty set denies access (fail closed).
      capabilities: mapToCapabilitySet(ref.capabilities),
    };
  }, [leagueId, refsData]);

  const switchLeague = useCallback(
    (nextLeagueId: string | null, ownerOrganizationId?: string) => {
      if (currentPlatform === null) {
        return;
      }

      // Clearing the league → drop the league scope and land on the org leagues
      // list (`/tours`), matching the league-sidebar back-row behavior. There are
      // no callers that rely on `restPath` being preserved when clearing.
      if (nextLeagueId === null) {
        removeStoredLeagueId();
        navigate(
          buildScopedPath(currentPlatform.id, currentOrganization?.id ?? null, null, "tours"),
          { replace: false }
        );
        return;
      }

      // Selecting a league preselects its owning org (league ⇒ org). Prefer the
      // owner passed in by the picker, then the resolved league's owner, then the
      // current org as a last resort.
      const ownerOrgId =
        ownerOrganizationId ?? league?.owner?.id ?? currentOrganization?.id ?? null;

      if (ownerOrgId === null) {
        return;
      }

      setStoredLeagueId(nextLeagueId);
      navigate(
        buildScopedPath(currentPlatform.id, ownerOrgId, nextLeagueId, scopedParams.restPath),
        { replace: false }
      );
    },
    [
      currentPlatform,
      currentOrganization?.id,
      league?.owner?.id,
      scopedParams.restPath,
      navigate,
      setStoredLeagueId,
      removeStoredLeagueId,
    ]
  );

  const loading = leagueId !== null && isLoading && !refsData;
  const isError = !!error;

  const contextValue = useMemo(
    () => ({
      league,
      loading,
      isError,
      switchLeague,
    }),
    [league, loading, isError, switchLeague]
  );

  return (
    <CurrentLeagueContext.Provider value={contextValue}>{children}</CurrentLeagueContext.Provider>
  );
};
